import { ApiDailyPuzzleSource } from "./api-daily-puzzle-source";
import { LocalDailyPuzzleSource } from "./local-daily-puzzle-source";
import type { DailyPuzzle } from "./types";

/**
 * Daily-puzzle resolution is pluggable: the SPA either calls
 * `CogQuest.Service.GET /api/v1/puzzle/today` (the production path,
 * authoritative because the puzzle is anchored on-chain) or derives
 * the puzzle locally from the UTC date (a self-contained mode for
 * offline dev, demos, and tests).
 *
 * The two implementations both produce the same {@link DailyPuzzleResult}
 * shape so the UI doesn't care which is wired up; the choice is made at
 * SPA startup via `VITE_COGQUEST_PUZZLE_SOURCE`.
 */

/// Status union the daily route renders against.
///
/// - `ok` carries a {@link DailyPuzzle} and a `stale` flag (true when
///   the server served a cached response under `stale-if-error`).
/// - `pending` means neither today nor yesterday is anchored on-chain
///   yet (API path only; never produced by the local source).
/// - `unavailable` means the API call failed and the server has no
///   cache; `retryAfterSeconds` mirrors the `Retry-After` header when
///   present (API path only).
export type DailyPuzzleResult =
  | { readonly status: "ok"; readonly puzzle: DailyPuzzle; readonly stale: boolean }
  | { readonly status: "pending" }
  | { readonly status: "unavailable"; readonly retryAfterSeconds: number | undefined };

/// Pluggable resolver for the daily puzzle. Implementations are
/// expected to be cheap to construct and safe to share.
export interface DailyPuzzleSource {
  fetchTodaysPuzzle(): Promise<DailyPuzzleResult>;
}

export type DailyPuzzleSourceMode = "api" | "local";

/**
 * Picks the {@link DailyPuzzleSource} implementation for this build.
 *
 * Defaults to the API source; pass `"local"` (or set
 * `VITE_COGQUEST_PUZZLE_SOURCE=local`) to derive the puzzle in-browser
 * from the UTC date with no backend dependency.
 */
export function createDailyPuzzleSource(
  mode: DailyPuzzleSourceMode = readDailyPuzzleSourceMode(),
): DailyPuzzleSource {
  return mode === "local" ? new LocalDailyPuzzleSource() : new ApiDailyPuzzleSource();
}

/// The env-configured default. Any value other than `local` falls
/// back to the API source so a misconfiguration never silently
/// downgrades a production deploy off the authoritative source.
export function readDailyPuzzleSourceMode(): DailyPuzzleSourceMode {
  return import.meta.env.VITE_COGQUEST_PUZZLE_SOURCE === "local" ? "local" : "api";
}
